/**
 * flashbookReport.ts — what a learner's read of a book adds up to.
 *
 * A book is not a capsule. A capsule is one screen of questions and its report is
 * a scorecard; a book is a hundred pages of reading and asking, read over several
 * sittings, and an answer at the end that says nothing but "72%" tells the learner
 * nothing they can act on. So this file exists to turn the raw result list the
 * player collects into a *report*: where the learner stands, where they are strong,
 * exactly which questions to look at again, and how their time went.
 *
 * Three decisions shape it.
 *
 *  - **Everything is derived, nothing is stored.** The inputs are the book and the
 *    results the player already keeps, so the report is computed rather than
 *    recorded and a book edited after a play still reports against what is on the
 *    page today. A result whose block no longer exists is not "missing" — it is
 *    stale, and iterating the *book* rather than the results is what makes that
 *    fall out for free.
 *  - **A claim needs evidence.** Naming a skill the learner is strong in from a
 *    single question is not insight, it is noise — so a strength needs more than
 *    one exercise behind it, and a "weakest area" is only named when something was
 *    actually got wrong. The rest is still *shown*, in the skill list, where it is
 *    data rather than a verdict.
 *  - **It carries facts, not sentences.** Every insight is a kind plus its
 *    numbers; the wording lives in the report component behind Lingui. A model that
 *    emitted English prose could not be translated, and one that emitted a single
 *    blended "score" could not tell the learner *which* exercise to redo.
 */
import {formatCorrectAnswer, formatUserAnswer} from '#/lib/answerFormat'
import {
  bookDirection,
  type FlashBookDirection,
  pageDirection,
  slideDirection,
} from '#/lib/flashbookDirection'
import {getSlideMaxPoints, NON_SCORED_TYPES} from '#/lib/scoring'
import {type Slide, SLIDE_TYPE_META} from '#/types/capsule'
import {
  type FlashBookBlockResult,
  type FlashBookPage,
  type FlashBookPageResult,
} from '#/types/flashbook'

/**
 * How well a body of work went, as a word rather than a number.
 *
 * The bands are deliberately few and wide, and each one has a *different* piece
 * of advice attached to it in the report — three shades of "quite good" would be
 * three ways of saying nothing.
 */
export type ReportBand =
  /** Every exercise answered, every one right. */
  | 'perfect'
  /** Answered nearly all of it, and mostly right. */
  | 'strong'
  /** Real progress with real gaps. */
  | 'steady'
  /** More wrong than right — the book wants another read. */
  | 'shaky'
  /** Nothing was scored (a reading-only book, or nothing attempted). */
  | 'unscored'

/** One block type, as a skill. */
export type SkillRow = {
  type: string
  /** The app's own label for the type (see `SLIDE_TYPE_META`). */
  label: string
  /** Exercises of this type in the book. */
  exercises: number
  /** How many were answered at all. */
  answered: number
  correct: number
  wrong: number
  /** Reachable points of this type in the book. */
  maxPoints: number
  earnedPoints: number
  /**
   * Correct over *answered* — never over the exercises in the book.
   *
   * Dividing by everything would score skipping as getting it wrong, and a
   * learner who stopped halfway down a page would be told their matching is
   * terrible rather than that they did not get to it. `skipped` carries that
   * fact instead, in its own number.
   */
  accuracy: number
  band: ReportBand
}

/** A question worth looking at again: wrong, partly right, or never answered. */
export type FocusEntry = {
  pageIndex: number
  pageTitle: string
  /**
   * Which way the page's own title is written.
   *
   * Carried separately from `direction` because a page and the question on it can
   * be written different ways — an Arabic page holding an English quotation is a
   * page of a workbook, not an accident — and the report prints both.
   */
  pageDirection: FlashBookDirection
  /** Which way this question is written (its prompt, its answers, its note). */
  direction: FlashBookDirection
  blockId: string
  type: string
  label: string
  /** The question, or the block's own opening words for a text-shaped type. */
  prompt: string
  /** The learner's answer as written, or `'—'` when there is none. */
  userAnswer: string
  correctAnswer: string
  explanation: string | null
  earnedPoints: number
  maxPoints: number
  timeSpentMs: number
  /** Some of it was right — a partial score, not a blank. */
  partial: boolean
  /** Never answered: the learner turned the page. */
  skipped: boolean
}

/** One page, as it went. */
export type PageRow = {
  index: number
  title: string
  /** Which way this page is written, for its title. */
  direction: FlashBookDirection
  blocks: number
  exercises: number
  answered: number
  correct: number
  earnedPoints: number
  maxPoints: number
  /** 0–100, of the page's reachable points. 100 on a page with none. */
  mastery: number
  timeSpentMs: number
  /** Something on this page was not mastered — it is worth another look. */
  needsWork: boolean
}

/** The tone an insight is drawn in, so the UI never has to judge the numbers. */
export type ReportTone = 'good' | 'warn' | 'info'

/** The exercise that took the learner longest, whichever way it went. */
export type SlowestEntry = {
  pageIndex: number
  pageTitle: string
  label: string
  prompt: string
  timeSpentMs: number
}

export type ReportInsight =
  | {kind: 'strongest'; tone: 'good'; skill: SkillRow}
  | {kind: 'weakest'; tone: 'warn'; skill: SkillRow}
  | {kind: 'hardestPage'; tone: 'warn'; page: PageRow}
  | {kind: 'skipped'; tone: 'warn'; count: number}
  | {kind: 'streak'; tone: 'good'; count: number}
  /** How long a question took, on average, and which one took the most. */
  | {
      kind: 'pace'
      tone: 'info'
      msPerExercise: number
      slowest: SlowestEntry | null
    }
  /** Only produced when some of the narration was left unheard. */
  | {kind: 'readings'; tone: 'info'; heard: number; total: number}

export type FlashBookReportModel = {
  score: {points: number; max: number; pct: number}
  band: ReportBand
  counts: {
    blocks: number
    exercises: number
    answered: number
    correct: number
    wrong: number
    partial: number
    skipped: number
    /** Reading blocks that carry a recording. */
    readings: number
    /** …of those, the ones the learner actually played. */
    readingsHeard: number
  }
  time: {
    totalMs: number
    msPerExercise: number
    /** The page that took the longest, or `null` for an empty book. */
    longestPage: PageRow | null
    /** The single exercise that took the longest, or `null` if none was timed. */
    slowest: SlowestEntry | null
  }
  /** Longest run of consecutive correct answers, in book order. */
  streak: number
  skills: SkillRow[]
  /** Perfect areas with more than one exercise behind them. */
  strengths: SkillRow[]
  /**
   * Every area that lost a question, ranked by what it cost (see
   * `rankWeaknesses`). The report names the top of this list; the skill list
   * shows all of them.
   */
  weaknesses: SkillRow[]
  /** Wrong, partial and skipped questions, in book order. */
  focus: FocusEntry[]
  pages: PageRow[]
  /** The page with the lowest mastery and something on it, if there is one. */
  hardestPage: PageRow | null
  insights: ReportInsight[]
}

/**
 * The languages whose writing runs right-to-left.
 *
 * Written as languages rather than as "which catalogues does the app ship today",
 * because that is the fact being asked about: Arabic is written right-to-left
 * whether or not its catalogue has been built yet. The app ships Arabic and no
 * other right-to-left language today; the rest are here so that adding one is a
 * catalogue rather than a bug hunt.
 */
const RTL_LANGUAGES = new Set([
  'ar',
  'arc',
  'ckb',
  'dv',
  'fa',
  'he',
  'ku',
  'nqo',
  'ps',
  'sd',
  'ug',
  'ur',
  'yi',
])

/**
 * Which way the report's *own* words are written.
 *
 * The report is a mixed surface, and this is the half of it that is not the book.
 * Its verdict, its insights, its labels and its numbers are app copy: they come
 * from the translation catalogue, so they are written in the reader's own language,
 * whatever language the book is in. A book's words are the other half — a prompt,
 * an answer, a page title — and they carry their own direction per block.
 *
 * The two have to be set separately, because a right-to-left *paragraph* holding an
 * English *sentence* is not merely untidy: the sentence's full stop and its numbers
 * jump to the wrong end, and "3 of 6 correct." reads as ".of 6 correct 3".
 */
export function copyDirection(
  locale: string | null | undefined,
): FlashBookDirection {
  const language = (locale ?? '').toLowerCase().split(/[-_]/)[0]
  return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr'
}

/**
 * The areas that lost questions, most costly first.
 *
 * Ordered by how many questions the type *cost* — the honest reading of "what went
 * wrong" — with the share lost and then the number of questions behind the claim as
 * tie-breaks. A type the learner never reached has no wrong answers and is not on
 * this list at all: in a book abandoned on page 4 of 60, naming everything they did
 * not get to as a weakness is a claim about where they stopped, not about what they
 * can do.
 */
function rankWeaknesses(skills: SkillRow[]): SkillRow[] {
  return skills
    .filter(s => s.wrong > 0)
    .sort(
      (a, b) =>
        b.wrong - a.wrong ||
        b.wrong / b.exercises - a.wrong / a.exercises ||
        b.exercises - a.exercises ||
        a.label.localeCompare(b.label),
    )
}

/** The most insights worth showing before a report becomes a wall of text. */
const INSIGHT_LIMIT = 5

/** A strength is only named when more than one exercise stands behind it. */
const STRENGTH_MIN_EXERCISES = 2

/** How many skills are named in the two headline groups. */
const HEADLINE_LIMIT = 2

/**
 * Whether a block is scored — the same question `getSlideMaxPoints` answers.
 *
 * Asked of `NON_SCORED_TYPES` rather than of a second list, so a block type that
 * becomes gradeable (or stops being) is counted here the moment it is counted
 * there, and the report can never disagree with the score the player saved.
 */
export function isScoredBlock(block: Slide): boolean {
  return !NON_SCORED_TYPES.has(block.type)
}

/** Whether a block is a reading with a voice attached — the narrated half. */
export function isNarratedBlock(block: Slide): boolean {
  return block.type === 'content' && !!block.readingAudioUrl
}

/**
 * One question, as a row on a list of things to look at again.
 *
 * Built here once because there are now two such lists — the report's own, and the
 * one a read that is still going has open (see `openFocusEntries`) — and the same
 * question must read the same way on both. They differ in *which* questions they
 * hold, never in how a question is phrased: the prompt, the direction, the answer
 * as the learner wrote it. Two builders would drift apart, and the drift would
 * show up as the same question phrased two ways on the cover and at the end.
 */
function focusEntryFor({
  block,
  result,
  pageIndex,
  pageTitle,
  pageDir,
}: {
  block: Slide
  result: FlashBookBlockResult | undefined
  pageIndex: number
  pageTitle: string
  pageDir: FlashBookDirection
}): FocusEntry {
  const earned = result?.points ?? 0
  const wasAnswered = !!result && result.isCorrect !== undefined
  return {
    pageIndex,
    pageTitle,
    pageDirection: pageDir,
    direction: slideDirection(block) ?? pageDir,
    blockId: block.id,
    type: block.type,
    label: typeLabel(block.type),
    prompt: blockPrompt(block),
    userAnswer: formatUserAnswer(block, result?.userAnswer),
    correctAnswer: formatCorrectAnswer(block),
    explanation: block.explanation?.trim() || null,
    earnedPoints: earned,
    maxPoints: getSlideMaxPoints(block),
    timeSpentMs: result?.timeSpentMs ?? 0,
    partial: earned > 0,
    skipped: !wasAnswered,
  }
}

/**
 * The questions a read has left open *so far*.
 *
 * The report's list is written for a book that has been read to the end: by then
 * the learner has been offered every exercise, so every one not correctly answered
 * is worth another look — including the ones they never reached. A read that
 * stopped on page 9 of 40 is a different question entirely, and answering it with
 * the report's list would be wrong in the one way that matters: the other
 * thirty-one pages are not *open*, they are *unread*, and a practice built from
 * them would be the rest of the book under another name.
 *
 * So a page with no result is not consulted at all — the learner was never there —
 * while a question they turned the page past *is* included, because they were
 * offered it and moved on. That is the report's own rule, cut to the part of the
 * book the read actually got to.
 */
export function openFocusEntries({
  pages,
  pageResults,
}: {
  pages: readonly FlashBookPage[]
  pageResults: readonly FlashBookPageResult[]
}): FocusEntry[] {
  const bookDir = bookDirection(pages)
  const open: FocusEntry[] = []

  pages.forEach((page, index) => {
    const pageResult = pageResults.find(r => r.pageId === page.id)
    if (!pageResult) return
    const pageDir = pageDirection(page.blocks) ?? bookDir
    const title = page.title?.trim() || ''
    for (const block of page.blocks) {
      if (!isScoredBlock(block)) continue
      const result = pageResult.blocks.find(b => b.blockId === block.id)
      if (result?.isCorrect === true) continue
      open.push(
        focusEntryFor({
          block,
          result,
          pageIndex: index,
          pageTitle: title,
          pageDir,
        }),
      )
    }
  })

  return open
}

/**
 * The block's own opening words, for a row that has to name the question.
 *
 * Not every type asks its question in `question`: a fill-in-the-blanks paragraph
 * *is* the question, a word search is its grid, a drag-the-words exercise is its
 * template. Falling back through the shapes the learner actually saw is what keeps
 * a focus row from reading "Drag words" with nothing under it.
 */
export function blockPrompt(block: Slide): string {
  const first = (...candidates: (string | undefined)[]) =>
    candidates.map(c => c?.trim()).find(c => !!c) ?? ''

  switch (block.type) {
    case 'content':
      return first(block.contentTitle, block.contentText)
    case 'fill_blanks':
      return first(block.fillBlanksParagraph)
    case 'drag_words':
      return first(block.dragWordsTemplate)
    case 'complex_fill_blank':
      return first(...(block.complexBlanks ?? []).map(b => b.sentence))
    case 'accordion':
      return first(...(block.accordionItems ?? []).map(i => i.title))
    case 'mark_words':
      return first(block.markWordsText)
    case 'find_words':
      return first(...(block.findWordsTargets ?? []))
    case 'matching':
      return first(...(block.pairs ?? []).map(p => p.left))
    case 'ordering':
      return first(...(block.orderItems ?? []).map(i => i.label))
    case 'fill_blank':
      return first(
        ...(block.blanks ?? []).map(b => `${b.before} ____ ${b.after}`),
      )
    default:
      return first(block.question)
  }
}

/**
 * The app's English type label, or the raw type for one it does not know.
 *
 * Exported because the class report names the same block types when it says which
 * kind of question a class lost points to; a second copy of this lookup would be a
 * second place for a type's name to drift.
 */
export function slideTypeLabel(type: string): string {
  return SLIDE_TYPE_META[type as keyof typeof SLIDE_TYPE_META]?.label ?? type
}

/** The app's English type label, or the raw type for one it does not know. */
function typeLabel(type: string): string {
  return slideTypeLabel(type)
}

/**
 * How well a body of work went, as a band.
 *
 * Shared with the class report on purpose: the two reports print the *same* words
 * for the same numbers, so a learner told their matching was "steady" and a teacher
 * told their class's matching was "shaky" can never be looking at the same work.
 */
export function bandFor({
  answered,
  correct,
  exercises,
}: {
  answered: number
  correct: number
  exercises: number
}): ReportBand {
  if (exercises === 0 || answered === 0) return 'unscored'
  if (correct === exercises && correct === answered) return 'perfect'
  const accuracy = correct / answered
  if (accuracy >= 0.75) return 'strong'
  if (accuracy >= 0.5) return 'steady'
  return 'shaky'
}

/**
 * Builds the whole report from the book and the results the player collected.
 *
 * `pageResults` may contain pages and blocks the book no longer has (a book edited
 * between two reads) and may be missing pages entirely (turned without answering).
 * Both are normal, and both are handled by walking the book and looking results
 * *up*, rather than walking the results and trusting them.
 */
export function buildFlashBookReport({
  pages,
  pageResults,
  elapsedMs,
}: {
  pages: readonly FlashBookPage[]
  pageResults: readonly FlashBookPageResult[]
  /** Wall-clock time of the read, which is not the sum of the page times. */
  elapsedMs: number
}): FlashBookReportModel {
  const resultForPage = (pageId: string) =>
    pageResults.find(r => r.pageId === pageId)
  /** The book's direction, for the writing on a page that cannot say for itself. */
  const bookDir = bookDirection(pages)

  const skills = new Map<string, SkillRow>()
  const focus: FocusEntry[] = []
  const rows: PageRow[] = []
  /** Every exercise in book order, for the streak and the pace. */
  const attempts: {correct: boolean; timeSpentMs: number}[] = []

  let blocks = 0
  let exercises = 0
  let answered = 0
  let correctCount = 0
  let wrong = 0
  let partial = 0
  let skipped = 0
  let readings = 0
  let readingsHeard = 0
  let points = 0
  let max = 0
  let slowest: SlowestEntry | null = null

  pages.forEach((page, index) => {
    const pageResult = resultForPage(page.id)
    const title = page.title?.trim() || ''
    const pageDir = pageDirection(page.blocks) ?? bookDir
    let pageExercises = 0
    let pageAnswered = 0
    let pageCorrect = 0
    let pagePoints = 0
    let pageMax = 0

    for (const block of page.blocks) {
      blocks += 1
      const result = pageResult?.blocks.find(b => b.blockId === block.id)

      if (isNarratedBlock(block)) {
        readings += 1
        // The player reports a reading the moment it is played; a reading block
        // with no result was never listened to, which is exactly what the count
        // is for.
        if (result) readingsHeard += 1
      }

      if (!isScoredBlock(block)) continue

      const blockMax = getSlideMaxPoints(block)
      const earned = result?.points ?? 0
      const isCorrect = result?.isCorrect === true
      const wasAnswered = !!result && result.isCorrect !== undefined

      exercises += 1
      pageExercises += 1
      max += blockMax
      points += earned
      pageMax += blockMax
      pagePoints += earned

      if (wasAnswered) {
        answered += 1
        pageAnswered += 1
        const timeSpentMs = result?.timeSpentMs ?? 0
        attempts.push({correct: isCorrect, timeSpentMs})
        // The slowest question is tracked over *every* exercise, right or wrong:
        // the one that ate the learner's time is worth naming even when they got
        // it, which is the opposite of what a list of only-wrong answers can say.
        if (timeSpentMs > (slowest?.timeSpentMs ?? 0)) {
          slowest = {
            pageIndex: index,
            pageTitle: title,
            label: typeLabel(block.type),
            prompt: blockPrompt(block),
            timeSpentMs,
          }
        }
      } else {
        skipped += 1
      }
      if (isCorrect) {
        correctCount += 1
        pageCorrect += 1
      } else if (wasAnswered) {
        wrong += 1
        if (earned > 0) partial += 1
      }

      const key = block.type
      const row =
        skills.get(key) ??
        ({
          type: key,
          label: typeLabel(key),
          exercises: 0,
          answered: 0,
          correct: 0,
          wrong: 0,
          maxPoints: 0,
          earnedPoints: 0,
          accuracy: 0,
          band: 'unscored' as ReportBand,
        } satisfies SkillRow)
      row.exercises += 1
      row.maxPoints += blockMax
      row.earnedPoints += earned
      if (wasAnswered) {
        row.answered += 1
        if (isCorrect) row.correct += 1
        else row.wrong += 1
      }
      skills.set(key, row)

      // Everything not fully right is worth another look — including the
      // question the learner never got to, which is the one a report of only
      // wrong answers would quietly drop.
      if (!isCorrect) {
        focus.push(
          focusEntryFor({
            block,
            result,
            pageIndex: index,
            pageTitle: title,
            pageDir,
          }),
        )
      }
    }

    const mastery =
      pageMax > 0
        ? Math.min(100, Math.round((pagePoints / pageMax) * 100))
        : 100
    rows.push({
      index,
      title,
      direction: pageDir,
      blocks: page.blocks.length,
      exercises: pageExercises,
      answered: pageAnswered,
      correct: pageCorrect,
      earnedPoints: pagePoints,
      maxPoints: pageMax,
      mastery,
      timeSpentMs: pageResult?.timeSpentMs ?? 0,
      needsWork: pageExercises > 0 && pageCorrect < pageExercises,
    })
  })

  for (const row of skills.values()) {
    row.accuracy = row.answered > 0 ? row.correct / row.answered : 0
    row.band = bandFor({
      answered: row.answered,
      correct: row.correct,
      exercises: row.exercises,
    })
  }

  const skillList = [...skills.values()].sort(
    (a, b) =>
      b.accuracy - a.accuracy ||
      b.exercises - a.exercises ||
      a.label.localeCompare(b.label),
  )

  const strengths = skillList
    .filter(s => s.band === 'perfect' && s.exercises >= STRENGTH_MIN_EXERCISES)
    .slice(0, HEADLINE_LIMIT)
  const weaknesses = rankWeaknesses(skillList)

  const pct =
    max > 0 ? Math.min(100, Math.max(0, Math.round((points / max) * 100))) : 100

  const msPerExercise =
    attempts.length > 0
      ? Math.round(
          attempts.reduce((a, t) => a + t.timeSpentMs, 0) / attempts.length,
        )
      : 0

  let streak = 0
  let run = 0
  for (const attempt of attempts) {
    run = attempt.correct ? run + 1 : 0
    if (run > streak) streak = run
  }

  const longestPage = rows.reduce<PageRow | null>(
    (best, row) => (!best || row.timeSpentMs > best.timeSpentMs ? row : best),
    null,
  )
  /**
   * The page that *cost* the most — and specifically not the page the learner
   * never got to.
   *
   * A page with nothing answered on it scores zero and would always "win" this,
   * which would turn "look at page 4 again" into "you stopped reading", a fact
   * the report already states in its own line. So a page has to have been worked
   * on to be called hard.
   */
  const hardestPage =
    rows
      .filter(row => row.answered > 0 && row.needsWork)
      .sort((a, b) => a.mastery - b.mastery || a.index - b.index)[0] ?? null

  const model: FlashBookReportModel = {
    score: {points, max, pct},
    band: bandFor({answered, correct: correctCount, exercises}),
    counts: {
      blocks,
      exercises,
      answered,
      correct: correctCount,
      wrong,
      partial,
      skipped,
      readings,
      readingsHeard,
    },
    time: {totalMs: elapsedMs, msPerExercise, longestPage, slowest},
    streak,
    skills: skillList,
    strengths,
    weaknesses,
    focus,
    pages: rows,
    hardestPage,
    insights: [],
  }

  model.insights = buildInsights(model).slice(0, INSIGHT_LIMIT)
  return model
}

/**
 * The sentences a learner should take away, most useful first.
 *
 * Ordered by what a learner can *do* with it: the areas that cost points, then the
 * question they spent longest on, then the encouragement. The cap is applied by
 * the caller, so a book with one page of one question gets one line rather than a
 * report padded out to a fixed length.
 */
function buildInsights(model: FlashBookReportModel): ReportInsight[] {
  const out: ReportInsight[] = []
  const {counts} = model

  // Naming the weakest area only means something when the type has been met more
  // than once: "your weakest skill is matching" from a single question is a
  // statement about the book, not about the learner. The search walks the whole
  // ranked list rather than the two the report headlines, so a book of only unique
  // questions still gets the one weakness it has evidence for.
  const weak = model.weaknesses.find(s => s.exercises >= STRENGTH_MIN_EXERCISES)
  if (weak) out.push({kind: 'weakest', tone: 'warn', skill: weak})

  if (model.hardestPage) {
    out.push({kind: 'hardestPage', tone: 'warn', page: model.hardestPage})
  }

  if (counts.skipped > 0) {
    out.push({kind: 'skipped', tone: 'warn', count: counts.skipped})
  }

  // A strength is even stricter, because it is a claim about the learner rather
  // than about the book.
  if (model.strengths.length > 0) {
    out.push({kind: 'strongest', tone: 'good', skill: model.strengths[0]})
  }

  if (model.streak >= 3) {
    out.push({kind: 'streak', tone: 'good', count: model.streak})
  }

  if (model.time.msPerExercise > 0) {
    out.push({
      kind: 'pace',
      tone: 'info',
      msPerExercise: model.time.msPerExercise,
      slowest: model.time.slowest,
    })
  }

  // Only worth a line when something was left unheard: "you listened to all four
  // recordings" is a compliment the learner can do nothing with.
  if (counts.readingsHeard < counts.readings) {
    out.push({
      kind: 'readings',
      tone: 'info',
      heard: counts.readingsHeard,
      total: counts.readings,
    })
  }

  return out
}
